import { Box, makeStyles, Typography } from "@material-ui/core";
import { PhoneOutlined, MailOutline } from "@material-ui/icons";
import React, { FC, ReactNode } from "react";

import { Agent } from "@/api/generated";
import OfferAchievement from "@/components/icon/OfferAchievement";
import OfferAdviceAndFollowup from "@/components/icon/OfferAdviceAndFollowup";
import OfferPartners from "@/components/icon/OfferPartners";
import OfferStrengthOfPartnerFinancialInstitutions from "@/components/icon/OfferStrengthOfPartnerFinancialInstitutions";
import OfferStrengthOfPropertiesForSale from "@/components/icon/OfferStrengthOfPropertiesForSale";
import { LinkifiedText } from "@/components/LinkifiedText";
import { SalesAchievementsCard } from "@/components/offer/SalesAchievementsCard";
import { PC_HEADER_HEIGHT } from "@/components/topBarStyles";
import { useMasterAgentSearchTagMap } from "@/hooks/useGetMasterData";
import theme from "@/theme";

import { AgentCompatibilitySection } from "./AgentCompatibilitySection";
import { AgentDetailHeader } from "./AgentDetailHeader";
import { AgentIntroduction } from "./AgentIntroduction";

const useStyles = makeStyles(() => ({
  columns: { display: "flex", flexDirection: "column", gap: 0, [theme.breakpoints.up("md")]: { gap: 24 } },
  desktopColumns: {
    [theme.breakpoints.up("md")]: { display: "grid", gridTemplateColumns: "384px minmax(0, 1fr)", gap: 40 },
    [theme.breakpoints.between("md", "md")]: { gridTemplateColumns: "300px minmax(0, 1fr)", gap: 24 },
  },
  side: { minWidth: 0 },
  stickySide: {
    [theme.breakpoints.up("md")]: {
      position: "sticky",
      top: `calc(${PC_HEADER_HEIGHT + 24}px + env(safe-area-inset-top, 0px))`,
      alignSelf: "start",
      display: "flex",
      flexDirection: "column",
      gap: 24,
      maxHeight: `calc(100vh - ${PC_HEADER_HEIGHT + 48}px - env(safe-area-inset-top, 0px))`,
      overflowY: "auto",
    },
  },
  // PC はひとこと・紹介写真をサイドバーのヒーロー直下に白いカードで置く（Figma は SP のみなので暫定）
  sideIntroduction: {
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: 24,
    borderRadius: 16,
  },
  body: { minWidth: 0, display: "flex", flexDirection: "column", gap: 0, [theme.breakpoints.up("md")]: { gap: 32 } },
  about: {
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 24,
    [theme.breakpoints.up("md")]: { borderRadius: 16 },
  },
  section: { display: "flex", flexDirection: "column", gap: 12 },
  heading: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.semantic["text/heading"],
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  subheading: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.semantic["action/primary"],
    display: "flex",
    alignItems: "center",
    gap: 8,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  companyIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, flexShrink: 0 },
  text: {
    fontSize: 14,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  achievements: {
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    [theme.breakpoints.up("md")]: { padding: 0, gap: 16 },
  },
  sales: {
    display: "flex",
    gap: 16,
    overflowX: "auto",
    paddingBottom: 8,
    "& .MuiCard-root": { backgroundColor: "transparent" },
    "& .MuiCardMedia-root": { borderRadius: 8, padding: 0 },
    "& .MuiCardContent-root": { padding: "8px 0", gap: 8 },
    "& .MuiCardContent-root > div": { order: 3, margin: 0 },
    "& .MuiCardContent-root > p:nth-child(2)": { fontSize: 14, fontWeight: 700, lineHeight: 1.3 },
    "& .MuiCardContent-root > p:nth-child(3)": { fontSize: 12, lineHeight: 1.5 },
  },
  company: {
    backgroundColor: theme.palette.semantic["surface/base"],
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    [theme.breakpoints.up("md")]: { padding: 0, gap: 16 },
  },
  companyContent: {
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: "4px 16px",
    borderRadius: 16,
    [theme.breakpoints.up("md")]: { padding: "8px 24px" },
  },
  companyItem: {
    padding: "20px 0",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    "& + &": { borderTop: `1px solid ${theme.palette.semantic["border/default"]}` },
  },
  contact: {
    margin: 24,
    padding: 16,
    borderRadius: 8,
    backgroundColor: theme.palette.semantic["state/selected-bg"],
    display: "flex",
    flexDirection: "column",
    gap: 8,
    fontSize: 14,
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { margin: 0 },
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    "& svg": { fontSize: 16, color: theme.palette.semantic["text/heading"] },
  },
}));

/**
 * 一覧とメッセージの両方で、プランナー情報全体を同じ部品から表示する。
 * 並びは Figma B-1: 基本情報 → ひとこと・紹介写真 → プロフィール → あなたとの相性 → 販売実績 → 会社情報。
 */
export const AgentDetails: FC<{
  agent: Agent;
  isMatched?: boolean;
  nameComponent?: "h1" | "h2";
  status?: ReactNode;
  /** 独立した詳細ページのPC表示で有効にする。埋め込み先は縦積みを維持する。 */
  desktopColumns?: boolean;
  sidebarActions?: ReactNode;
  variant?: "default" | "certified";
  /** 遷移元の一覧（相性プランナー一覧・検索一覧）での順位。上位3人だけ相性スコアに王冠を出す */
  compatibilityRank?: number;
}> = ({
  agent,
  isMatched = !!agent.is_matched,
  nameComponent,
  status,
  desktopColumns = false,
  sidebarActions,
  variant = "default",
  compatibilityRank,
  children,
}) => {
  const classes = useStyles();
  const { tagIdToNameMap } = useMasterAgentSearchTagMap();
  const companyItems = [
    { title: "会社の実績", text: agent?.company?.achievements, icon: <OfferAchievement fill="currentColor" /> },
    { title: "販売物件/物件買取の強み", text: agent?.company?.strengths_of_properties_for_sale_and_purchase, icon: <OfferStrengthOfPropertiesForSale fill="currentColor" /> },
    { title: "提携金融機関", text: agent?.company?.strengths_of_partner_financial_institutions, icon: <OfferStrengthOfPartnerFinancialInstitutions fill="currentColor" /> },
    { title: "販売後の物件管理", text: agent?.company?.strengths_of_properties_for_management, icon: <OfferPartners fill="currentColor" /> },
    { title: "自社ならではのアピールポイント", text: agent?.company?.appeal_point, icon: <OfferAdviceAndFollowup fill="currentColor" /> },
  ].filter((item) => item.text);
  const contact = isMatched && (
    <Box className={classes.contact} data-testid="planner-contact">
      <Box className={classes.contactRow}><PhoneOutlined /><span>{agent.phone_number}</span></Box>
      <Box className={classes.contactRow}><MailOutline /><span>{agent.email}</span></Box>
    </Box>
  );
  return (
    <Box
      className={`${classes.columns} ${desktopColumns ? classes.desktopColumns : ""}`}
      data-testid="planner-details"
      data-agent-id={agent.id}
    >
      <Box
        className={`${classes.side} ${desktopColumns ? classes.stickySide : ""}`}
        data-testid="planner-details-sidebar"
      >
        <AgentDetailHeader
          agent={agent}
          variant={variant === "certified" ? "certified" : "planner"}
          showBeginnerMessage={false}
          strengthLabels={(agent.company?.company_agent_search_tag_ids ?? []).map((tagId) => tagIdToNameMap[tagId] || `タグID: ${tagId}`)}
          nameComponent={nameComponent}
        />
        {desktopColumns && <AgentIntroduction agent={agent} className={classes.sideIntroduction} />}
        {desktopColumns && contact}
        {sidebarActions}
      </Box>
      <Box className={classes.body} data-testid="planner-details-body">
        {!desktopColumns && contact}
        {status}
        <Box className={classes.about} data-testid="planner-profile">
          {!desktopColumns && <AgentIntroduction agent={agent} />}
          <Box className={classes.section}>
            <Typography component="h2" className={classes.heading}>
              プロフィール
            </Typography>
            <Typography className={classes.text}>
              <LinkifiedText text={agent.profile} />
            </Typography>
          </Box>
        </Box>
        <AgentCompatibilitySection radar={agent.compatibility_radar} variant={variant} rank={compatibilityRank} />
        {agent.company?.sales_achievements?.length > 0 && (
          <Box className={classes.achievements} data-testid="planner-sales">
            <Typography component="h2" className={classes.heading}>
              直近の販売実績
            </Typography>
            <Box className={classes.sales}>
              {agent.company.sales_achievements.map((achievement, index) => (
                <SalesAchievementsCard
                  key={index}
                  profileImagePath={achievement.file_url || ""}
                  propertyName={achievement.achievement_name || "-"}
                  propertyArea={achievement.achievement_area || "-"}
                  propertyIncome={achievement.achievement_price || 0}
                  incomeLayout="inline"
                />
              ))}
            </Box>
          </Box>
        )}
        <Box className={classes.company} data-testid="planner-company">
          <Typography component="h2" className={classes.heading}>
            会社情報
          </Typography>
          <Box className={classes.companyContent}>
            {companyItems.map((item) => (
              <Box className={classes.companyItem} key={item.title}>
                <Typography component="h3" className={classes.subheading}>
                  <span className={classes.companyIcon} aria-hidden="true">{item.icon}</span>
                  {item.title}
                </Typography>
                <Typography className={classes.text}>
                  <LinkifiedText text={item.text} />
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        {children}
      </Box>
    </Box>
  );
};
